import * as React from "react";
import { SearchRegular, DismissRegular } from "@fluentui/react-icons";
import { useActivityLogPopupStyles } from "../styles/activityLogPopup.styles";
import { formatRelativeTime, extractPrNumber } from "./Sidebar";
import type { ActionLogEntry } from "../api/models/ApiModels";

type FilterTag = "all" | "scout" | "heal";

interface ActivityLogPopupProps {
    items: ActionLogEntry[];
    search: string;
    onSearchChange: (value: string) => void;
    filter: FilterTag;
    onFilterChange: (value: FilterTag) => void;
    loading?: boolean;
    onClose: () => void;
    onViewHealReport?: (entry: ActionLogEntry) => void;
    onViewScoutReport?: (entry: ActionLogEntry) => void;
}

export const ActivityLogPopup: React.FC<ActivityLogPopupProps> = ({
    items,
    search,
    onSearchChange,
    filter,
    onFilterChange,
    loading,
    onClose,
    onViewHealReport,
    onViewScoutReport,
}) => {
    const s = useActivityLogPopupStyles();

    // Lock background scroll when popup is open
    React.useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = ""; };
    }, []);

    // Close on Escape
    React.useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [onClose]);

    // Server-side search/filter is the source of truth; `items` is the already-filtered list.
    const filtered = items;

    const tagClass = (action: string): string => {
        switch (action.toLowerCase()) {
            case "heal": return `${s.tag} ${s.tagHeal}`;
            default:     return `${s.tag} ${s.tagScout}`;
        }
    };

    const chipClass = (t: FilterTag): string => {
        if (t === filter) return `${s.chip} ${s.chipActive}`;
        if (t === "scout") return `${s.chip} ${s.chipScout}`;
        if (t === "heal") return `${s.chip} ${s.chipHeal}`;
        return s.chip;
    };

    return (
        <div className={s.overlay} onClick={onClose}>
            <div className={s.modal} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className={s.header}>
                    <div className={s.titleGroup}>
                        <span className={s.title}>Activity Log</span>
                        <span className={s.badge}>{items.length}</span>
                    </div>

                    <div className={s.searchBar}>
                        <SearchRegular className={s.searchIcon} />
                        <input
                            className={s.searchInput}
                            placeholder="Search"
                            value={search}
                            onChange={e => onSearchChange(e.target.value)}
                        />
                    </div>

                    <div className={s.chipGroup}>
                        <button className={chipClass("all")} onClick={() => onFilterChange("all")}>All</button>
                        <button className={chipClass("scout")} onClick={() => onFilterChange("scout")}>Scout</button>
                        <button className={chipClass("heal")} onClick={() => onFilterChange("heal")}>Heal</button>
                    </div>

                    <button className={s.closeBtn} onClick={onClose} aria-label="Close">
                        <DismissRegular />
                    </button>
                </div>

                <div className={s.divider} />

                {/* Item list */}
                <div
                    className={s.list}
                    style={{ opacity: loading ? 0.5 : 1, transition: "opacity 0.15s" }}
                >
                    {filtered.length === 0 && (
                        <div className={s.emptyState}>
                            {loading ? "Loading…" : "No activity found."}
                        </div>
                    )}
                    {filtered.map(entry => {
                        const action = entry.action.toLowerCase();
                        const headline = entry.intent ?? entry.title;
                        const prNumber = entry.link ? extractPrNumber(entry.link) : null;
                        return (
                            <div key={entry.id} className={s.item}>
                                <div className={s.itemContent}>
                                    <span className={tagClass(entry.action)}>
                                        {entry.action.charAt(0).toUpperCase() + entry.action.slice(1)}
                                    </span>
                                    <div className={s.itemTextGroup}>
                                        <span className={s.itemTitle}>{headline}</span>
                                        <div className={s.itemMeta}>
                                            {entry.description && (
                                                <span className={s.itemDetail}>{entry.description}</span>
                                            )}
                                            {prNumber && (
                                                entry.link ? (
                                                    <a
                                                        href={entry.link}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className={s.prLink}
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        <span className={s.prNumber}>#{prNumber}</span>
                                                        <span className={s.prTitle}>{entry.title}</span>
                                                    </a>
                                                ) : (
                                                    <span className={s.prLinkInert}>
                                                        <span className={s.prNumber}>#{prNumber}</span>
                                                        <span className={s.prTitle}>{entry.title}</span>
                                                    </span>
                                                )
                                            )}
                                            {onViewHealReport && action === "heal" && (
                                                <span className={s.prLink} onClick={() => onViewHealReport(entry)}>
                                                    🩺 View Heal Report
                                                </span>
                                            )}
                                            {onViewScoutReport && action === "scout" && (
                                                <span className={s.prLink} onClick={() => onViewScoutReport(entry)}>
                                                    🔍 View Scout Report
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <span className={s.itemTime}>{formatRelativeTime(entry.createdDateTime)}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
